import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SHOW_SIGN_IN } from '../config/launch';
import { WordmarkLogo } from './Logo';
import { ArchImage, Label, Mullions } from './System';

const LINK = 'inline-flex min-h-[40px] items-center text-glass-light transition-colors hover:text-limestone';

function FooterColumn({ title, children }) {
  return (
    <div>
      <Label dark className="mb-3">{title}</Label>
      <ul className="m-0 list-none border-t border-limestone/20 p-0 pt-2 text-[15px]">{children}</ul>
    </div>
  );
}

// The closing curtain wall. On Home, the demo form's panel crosses into its top edge, so it starts lower.
function Footer() {
  const { pathname } = useLocation();
  const isHome = pathname === '/';

  return (
    <footer className="on-dark relative overflow-hidden bg-wall text-limestone">
      <div aria-hidden="true" className="absolute inset-x-0 top-0 h-[420px]">
        <ArchImage name="skyline" settle={false} sizes="100vw" className="h-full w-full !bg-wall opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-b from-wall/55 via-wall/80 to-wall" />
      </div>
      <Mullions dark />
      <div className={`shell relative pb-8 ${isHome ? 'pt-[120px] lg:pt-[250px]' : 'pt-[120px] lg:pt-[170px]'}`}>
        <div className="grid-12 gap-y-12">
          <div className="col-span-4 lg:col-span-4">
            <Link to="/" aria-label="Sidwell — home" className="inline-flex min-h-[44px] items-center">
              <WordmarkLogo onDark className="h-[44px] w-auto" />
            </Link>
            <p className="mt-6 font-display text-[clamp(26px,2.4vw,36px)] font-light leading-[1.15]">Ground every answer.</p>
            <p className="mt-3 max-w-[38ch] text-[15px] text-glass-light">
              Ask in plain English. See the working behind every number.
            </p>
          </div>
          <nav aria-label="Footer" className="col-span-4 grid grid-cols-1 gap-x-7 gap-y-10 sm:grid-cols-3 lg:col-span-7 lg:col-start-6">
            <FooterColumn title="Platform">
              <li><Link className={LINK} to="/conversational-bi">Conversational BI</Link></li>
              <li><Link className={LINK} to="/company-data-cloud">Company Data Cloud</Link></li>
              <li><Link className={LINK} to="/pushdown-sql-engine">Pushdown SQL Engine</Link></li>
            </FooterColumn>
            <FooterColumn title="Resources">
              <li><Link className={LINK} to="/documentation">Documentation</Link></li>
              <li><Link className={LINK} to="/innovation">Innovation &amp; Architecture</Link></li>
              <li><Link className={LINK} to="/security">Security &amp; Privacy</Link></li>
              <li><Link className={LINK} to="/faq">FAQ</Link></li>
            </FooterColumn>
            <FooterColumn title="Company">
              <li><Link className={LINK} to="/about">About Sidwell</Link></li>
              <li><a className={LINK} href="mailto:hello@sidwell.ai">Contact</a></li>
              {SHOW_SIGN_IN && <li><Link className={LINK} to="/signin">Sign In</Link></li>}
              <li><Link className={LINK} to="/terms">Terms of Service</Link></li>
            </FooterColumn>
          </nav>
        </div>
        <div className="mt-16 flex flex-col justify-between gap-2 border-t border-limestone/20 pt-5 text-[13px] text-glass-light sm:flex-row">
          <span>Answers you can defend.</span>
          <span className="figure">© {new Date().getFullYear()} Sidwell Intelligence Systems Inc.</span>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
